import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { resources } from '../../core/services/resources';
import { PageHeader } from '../../shared/components/PageHeader';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { formatDate } from '../../shared/utils/format';
import type { GridColDef } from '@mui/x-data-grid';
import type { ReportTemplate } from '../../core/types/data-models';

export function LineOfBusinessDetail() {
  const { id } = useParams();
  const lobId = Number(id);
  const navigate = useNavigate();

  const lob = useQuery({
    queryKey: ['lines-of-business', lobId],
    queryFn: () => resources.linesOfBusiness.get(lobId, ['lineOfBizBrands']),
    enabled: Number.isFinite(lobId),
  });

  // Same cache key as LinesOfBusinessPage / LineOfBizSelect so we share the
  // tenant's LOB list. Cheap parent-name resolution with no extra request.
  const lobLookup = useQuery({
    queryKey: ['lines-of-business', 'select'],
    queryFn: () => resources.linesOfBusiness.list({ pageSize: 200, sort: 'name' }),
  });

  // Tenants are global, not tenant-scoped, so we can resolve any tenantId
  // to its display name. Cap at 200 — bump if customer count grows past it.
  const tenants = useQuery({
    queryKey: ['tenants', 'select'],
    queryFn: () => resources.tenants.list({ pageSize: 200, sort: 'name' }),
  });

  const tenantName = useMemo(() => {
    const tid = lob.data?.tenantId;
    if (tid == null) return null;
    return tenants.data?.data.find((t) => t.id === tid)?.name ?? `#${tid}`;
  }, [lob.data?.tenantId, tenants.data]);

  const parentLobName = useMemo(() => {
    const pid = lob.data?.parentId;
    if (pid == null) return null;
    return lobLookup.data?.data.find((l) => l.id === pid)?.name ?? `#${pid}`;
  }, [lob.data?.parentId, lobLookup.data]);

  const reportTemplates = useQuery({
    queryKey: ['report-templates', { lobId }],
    queryFn: () =>
      resources.reportTemplates.list({
        page: 1,
        pageSize: 100,
        filter: { lineOfBizId: lobId },
        sort: '-id',
      }),
    enabled: Number.isFinite(lobId),
  });

  const reportColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      renderCell: (p) => <RouterLink to={`/report-templates/${p.row.id}`}>{p.value as string}</RouterLink>,
    },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'defaultPriority', headerName: 'Priority', width: 120 },
    { field: 'createdAt', headerName: 'Created', width: 180, valueFormatter: (v) => formatDate(v as string) },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={lob.data?.name ?? `Line of business #${lobId}`}
        description={lob.data?.description}
        actions={<Button onClick={() => navigate(-1)}>Back</Button>}
      />

      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>Details</Typography>
            <Stack direction="row" spacing={4} flexWrap="wrap">
              <Stat label="Tenant" value={tenantName ?? '—'} />
              <Stat label="Parent LOB" value={parentLobName ?? '—'} />
              <Stat label="Created" value={formatDate(lob.data?.createdAt)} />
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Report templates</Typography>
          <DataTable<ReportTemplate>
            rows={reportTemplates.data?.data ?? []}
            columns={reportColumns}
            loading={reportTemplates.isFetching}
            paginationMode="client"
          />
        </Box>
      </Stack>
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );
}
